import { motion } from "framer-motion";
import { AlertCircle, AlertTriangle } from "lucide-react";
import { NoDataBadge } from "@/components/ui/noDataBadge";
import { cn } from "@/lib/utils";

interface DistributionBarProps {
	breakdown: Dryp.IAndISummaryParts;
	className?: string;
	showValues?: boolean;
}

interface Segment {
	value: number;
	ratio: number;
	color: string;
}

export const getBreakdownSegments = (breakdown: Dryp.IAndISummaryParts): Segment[] => {
	const { waste, infiltration, inflow } = breakdown;
	const wasteWaterRatio = waste.ratio?.value || 0;
	const infiltrationRatio = infiltration.ratio?.value || 0;
	const inflowRatio = inflow.ratio?.value || 0;

	const segments = [
		{ value: waste.flow?.value || 0, ratio: wasteWaterRatio, color: "hsl(var(--chart-wastewater))" },
		{ value: infiltration.flow?.value || 0, ratio: infiltrationRatio, color: "hsl(var(--chart-infiltration))" },
		{ value: inflow.flow?.value || 0, ratio: inflowRatio, color: "hsl(var(--chart-rain))" },
	];
	return segments;
};

export const hasBreakdownInvalidData = (breakdownSegments: Segment[]) => breakdownSegments.some(s => s.ratio === 0);

export const DistributionBar: React.FC<DistributionBarProps> = ({ breakdown, showValues = false, className }) => {
	const segments = getBreakdownSegments(breakdown);

	const hasInvalid = hasBreakdownInvalidData(segments);
	if (hasInvalid) {
		return <NoDataBadge />;
	}
	return (
		<div className={cn("space-y-0", className)}>
			<div className="flex h-3 w-full overflow-hidden rounded">
				{hasInvalid ? (
					<div className="flex h-full w-full items-center justify-center  bg-gray-100 text-gray-500 text-[9px] gap-1">
						<AlertCircle className="w-2 h-2 text-red-400" />
						Missing configuration
					</div>
				) : (
					segments.map((s, index) => (
						<motion.div
							key={index}
							className="h-full flex-none"
							style={{ backgroundColor: s.color }}
							initial={{ width: "0%" }}
							animate={{ width: `${s.ratio}%` }}
							transition={{
								duration: 0.7,
								ease: "easeOut",
								delay: index * 0.2,
							}}
						/>
					))
				)}
			</div>

			{showValues && (
				<div className="flex gap-2 mt-1">
					{segments.map((s, i) => {
						const isNeg = s.value < 0;
						return (
							<div key={i} className="flex items-center gap-1">
								<div className="rounded-full w-2 h-2" style={{ backgroundColor: s.color }} />
								<div
									className={cn("text-[11px] tabular-nums text-gray-500", isNeg && "text-amber-700")}
								>
									{s.ratio.toFixed(1)}%
								</div>
								{isNeg && <AlertTriangle className="w-3 h-3 text-amber-600" />}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};
